import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import AlignJustifySvg from 'images/v5/icons/align-justify.svg';
import ChevronDownSvg from 'images/v5/icons/chevron-down.svg';
import FilterSvg from 'images/v5/icons/filter-lines.svg';
import GridSvg from 'images/v5/icons/grid-01.svg';
import LoopSvg from 'images/v5/icons/search-md.svg';
import Button from 'layout/atoms/Button/Button';
import Card from 'layout/atoms/Card/Card';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import InfoBox from 'layout/atoms/InfoBox/InfoBox';
import Input from 'layout/atoms/Input/Input';
import Loader from 'layout/atoms/Loader/Loader';
import ComponentCard from 'layout/molecules/ComponentCard/ComponentCard';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import FiltersBar from 'layout/molecules/FiltersBar/FiltersBar';
import Grid from 'layout/molecules/Grid/Grid';
import parseComponentsByCategory from 'parsers/specification/parseComponentsByCategory';
import { parseProductCard } from 'parsers/specification/parseProduct';
import { closeSidebar, openModal, toggleSpecPreviewComponents } from 'store/layoutSlice';
import { fetchPreviewComponents, selectSpecificationPreviewStatus, } from 'store/specificationPreviewSlice';
import { fetchSuppliers, selectSuppliers } from 'store/suppliersSlice';
import getCategoryLabel from 'utils/getCategoryLabel';
function Components() {
    const dispatch = useDispatch();
    const history = useHistory();
    const { t, i18n } = useTranslation(['specification', 'common']);
    const layout = useSelector((state) => state.layout);
    const specificationDetails = useSelector((state) => state.specificationPreview.item);
    const status = useSelector(selectSpecificationPreviewStatus);
    const [searchQuery, setSearchQuery] = useState('');
    const [supplierFilter, setSupplierFilter] = useState(null);
    const suppliersData = useSelector(selectSuppliers);
    useEffect(() => {
        if (status.fetchComponents === 'idle' && (specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.id)) {
            dispatch(fetchPreviewComponents({ specificationId: specificationDetails.id }));
        }
    }, [dispatch, status.fetchComponents, specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.id]);
    useEffect(() => {
        dispatch(fetchSuppliers());
    }, [dispatch]);
    if (status.fetchComponents === 'idle' || status.fetchComponents === 'loading') {
        return React.createElement(Loader, null);
    }
    const components = specificationDetails.components.edges.map((item) => {
        var _a;
        const component = item.node;
        const productSnapshot = component === null || component === void 0 ? void 0 : component.productSnapshot;
        const product = productSnapshot === null || productSnapshot === void 0 ? void 0 : productSnapshot.product;
        const variants = productSnapshot === null || productSnapshot === void 0 ? void 0 : productSnapshot.variantSnapshots.edges.map((item) => item.node);
        return Object.assign(Object.assign(Object.assign({}, component), product), { id: component.id, name: component.name, producerCode: product === null || product === void 0 ? void 0 : product.producerCode, supplier: {
                fullName: productSnapshot === null || productSnapshot === void 0 ? void 0 : productSnapshot.supplierFullName,
                shortName: productSnapshot === null || productSnapshot === void 0 ? void 0 : productSnapshot.supplierFullName,
            }, variants, activeProductVariantsCount: variants.length, productVariantsCount: variants.length, status: (_a = component === null || component === void 0 ? void 0 : component.productSnapshot) === null || _a === void 0 ? void 0 : _a.product.status });
    });
    const filteredComponents = components.filter((component) => {
        var _a, _b;
        const matchesSearch = (_a = component.name) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(searchQuery);
        const matchesSupplier = supplierFilter
            ? ((_b = component.supplier) === null || _b === void 0 ? void 0 : _b.shortName) === supplierFilter
            : true;
        return matchesSearch && matchesSupplier;
    });
    const categorizedComponents = parseComponentsByCategory(filteredComponents);
    const handleSpecificationLink = (route) => {
        if ((specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.state) === 'DRAFT') {
            dispatch(openModal({
                name: 'specificationCreator',
                data: { id: specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.id, activeTab: 'components' },
            }));
        }
        else {
            dispatch(closeSidebar({ name: 'specificationPreview' }));
            setTimeout(() => history.push(route), 300 - 10);
        }
    };
    const handleSearchFilter = (e) => {
        setSearchQuery(e.target.value.toLowerCase());
    };
    const handleSupplierFilter = (shortName) => {
        if (shortName !== supplierFilter) {
            setSupplierFilter(shortName);
        }
        else {
            setSupplierFilter(null);
        }
    };
    return (React.createElement(Grid, { gap: 24 },
        React.createElement(InfoBox, { type: "info", text: t('components_count', { count: components.length }), aside: specificationDetails.state !== 'PRODUCTION' && (React.createElement(Button, { theme: "ghost", onClick: () => handleSpecificationLink(`/specifications/details/${specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.id}/components`) }, t('common:maintenance'))), noBorder: true }),
        React.createElement(FiltersBar, { noPadding: true, mainButton: React.createElement(Button, { theme: "border", shape: "squared", leftIcon: layout.specPreviewComponents === 'grid' ? React.createElement(AlignJustifySvg, null) : React.createElement(GridSvg, null), onClick: () => dispatch(toggleSpecPreviewComponents()) }), searchInput: React.createElement(Input, { size: "s", leftIcon: React.createElement(LoopSvg, null), placeholder: t('common:search'), onChange: (e) => handleSearchFilter(e) }) },
            React.createElement(Dropdown, { trigger: React.createElement(Button, { theme: "border", leftIcon: React.createElement(FilterSvg, null), rightIcon: React.createElement(ChevronDownSvg, null) }, supplierFilter || t('suppliers_base')) }, suppliersData.status === 'succeeded' ? (suppliersData.items.map((supplier) => (React.createElement(Button, { key: supplier.shortName, align: "left", theme: supplierFilter === supplier.shortName ? 'fill' : 'ghost', onClick: () => handleSupplierFilter(supplier.shortName) }, supplier.shortName)))) : (React.createElement(Loader, { size: "s" })))),
        categorizedComponents.length === 0 && (React.createElement(EmptyState, { size: "s", header: t('common:no_results_header'), subheader: t('common:no_results_subheader') })),
        categorizedComponents.map((category) => (React.createElement(Grid, { key: category.categoryId, gap: 12 },
            React.createElement("h3", null, getCategoryLabel(category, i18n)),
            React.createElement(Grid, { columns: layout.specPreviewComponents === 'list' ? 1 : 2 }, category.items.map((component) => component.productSnapshot ? (React.createElement(ComponentCard, { key: component === null || component === void 0 ? void 0 : component.id, product: parseProductCard(component, i18n), componentId: component.id, onlyActiveVariants: true, deselectable: true, layout: layout.specPreviewComponents, disabled: specificationDetails.state === 'PRODUCTION', specCreator: true, noEdit: true })) : (React.createElement(Card, null,
                React.createElement("p", null, "Unnasigned Component"))))))))));
}
export default Components;
